import React, { useEffect, useState } from "react";
import styles from "../styles/mobilebottomcta.module.css";

/**
 * 하단 상담·대기 CTA 바 — `수능선배 홈페이지.dc.html` 셸 이식.
 * - variant="mobile": 하단 바(잔여석 카피 + '상담 · 대기 신청' + '빠른상담' 소형 버튼). 히어로를 지나면 등장.
 * - variant="pc":     하단 플로팅 바(모집중 태그 + 메시지 + '빠른 상담' + '상담 · 대기 신청하기').
 *                     헤더가 숨는 순간(아래로 스크롤) 등장 → 화면엔 항상 CTA가 하나만(핸드오프).
 * '상담 · 대기 신청'은 공용 ConsultSheet(onOpenConsult), '빠른상담'은 전화·카톡 모달을 연다.
 */

const TEL = "tel:1668-5786";
const KAKAO_URL = "https://pf.kakao.com/_Whxexgxj/chat";

const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.27a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z" />
    </svg>
);
const KakaoIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 4C7.03 4 3 7.13 3 10.99c0 2.42 1.62 4.55 4.06 5.77-.18.64-.65 2.36-.74 2.73-.12.46.17.45.35.33.14-.09 2.27-1.54 3.19-2.17.36.05.74.08 1.14.08 4.97 0 9-3.13 9-7C21 7.13 16.97 4 12 4z" />
    </svg>
);

/** 빠른상담(전화·카톡) 시트 — 하단 CTA와 모바일 네비의 헤드셋 버튼이 공유한다. */
export const QuickConsultSheet: React.FC<{ open: boolean; onClose: () => void; variant?: "mobile" | "pc" }> = ({ open, onClose, variant = "mobile" }) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    return (
        <div className={`${styles.quick} ${variant === "pc" ? styles.quickPc : ""} ${open ? styles.isOpen : ""}`} aria-hidden={!open}>
            <div className={styles.quickScrim} onClick={onClose} />
            <div className={styles.quickPanel} role="dialog" aria-modal="true" aria-label="빠른 상담">
                <div className={styles.quickHandle} />
                <button type="button" className={styles.quickClose} aria-label="닫기" onClick={onClose}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                </button>
                <h3 className={styles.quickTitle}>예약 없이 <b>바로 상담받으세요</b></h3>
                <p className={styles.quickSub}>궁금한 점, 지금 편하게 물어보세요.</p>
                <div className={styles.quickOpts}>
                    <a href={TEL} className={styles.quickOpt} onClick={onClose}>
                        <span className={styles.quickOptArt}><PhoneIcon /></span>
                        <span className={styles.quickOptTxt}>
                            <span className={styles.quickOptLabel}>전화로 상담하기</span>
                            <span className={styles.quickOptHint}>지금 바로 연결해 드려요</span>
                        </span>
                    </a>
                    <a href={KAKAO_URL} target="_blank" rel="noopener noreferrer" className={styles.quickOpt} onClick={onClose}>
                        <span className={styles.quickOptArt}><KakaoIcon /></span>
                        <span className={styles.quickOptTxt}>
                            <span className={styles.quickOptLabel}>카카오톡으로 상담하기</span>
                            <span className={styles.quickOptHint}>편한 시간에 답장 받으세요</span>
                        </span>
                    </a>
                </div>
            </div>
        </div>
    );
};

const MobileBottomCta: React.FC<{ onOpenConsult?: () => void; variant?: "mobile" | "pc" }> = ({ onOpenConsult, variant = "mobile" }) => {
    const [visible, setVisible] = useState(false);
    const [quickOpen, setQuickOpen] = useState(false);

    // 등장 로직: 모바일=히어로 지나면 노출 / PC=아래로 스크롤(헤더 숨김)시 노출, 위로 스크롤·상단이면 숨김.
    useEffect(() => {
        let lastY = window.scrollY;
        let ticking = false;
        const THRESHOLD = 8;
        const onScroll = () => {
            const y = window.scrollY;
            const vh = window.innerHeight;
            const pastHero = y > Math.max(320, vh * 0.55);
            if (variant === "pc") {
                if (y < 32) setVisible(false);
                else if (y > lastY + THRESHOLD && pastHero) setVisible(true);
                else if (y < lastY - THRESHOLD) setVisible(false);
            } else {
                setVisible(pastHero);
            }
            if (Math.abs(y - lastY) >= THRESHOLD) lastY = y;
            ticking = false;
        };
        onScroll();
        const handler = () => { if (!ticking) { requestAnimationFrame(onScroll); ticking = true; } };
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, [variant]);

    const quick = <QuickConsultSheet open={quickOpen} onClose={() => setQuickOpen(false)} variant={variant} />;

    if (variant === "pc") {
        return (
            <>
                <div className={`${styles.pcBar} ${visible ? styles.isVisible : ""}`} aria-label="상담 · 대기 신청">
                    <div className={styles.pcInner}>
                        <div className={styles.pcMsgWrap}>
                            <span className={styles.pcTag}>모집중</span>
                            <span className={styles.pcMsg}>지점별 잔여석이 얼마 남지 않았어요</span>
                        </div>
                        <div className={styles.pcBtns}>
                            <button type="button" className={styles.pcQuick} onClick={() => setQuickOpen(true)}>빠른 상담</button>
                            <button type="button" className={styles.pcPrimary} onClick={() => onOpenConsult?.()}>상담 · 대기 신청하기</button>
                        </div>
                    </div>
                </div>
                {quick}
            </>
        );
    }

    return (
        <>
            <aside className={`${styles.bottomCta} ${visible ? styles.isVisible : ""}`} aria-label="상담 · 대기 신청">
                <div className={styles.bottomCtaLabel}>잔여석이 얼마 남지 않았어요</div>
                <div className={styles.bottomRow}>
                    <button type="button" className={styles.bottomCtaBtn} onClick={() => onOpenConsult?.()}>상담 · 대기 신청</button>
                    <button type="button" className={styles.bottomQuick} onClick={() => setQuickOpen(true)} aria-label="빠른 상담 — 전화·카톡">
                        <PhoneIcon />
                        <span>빠른상담</span>
                    </button>
                </div>
            </aside>
            {quick}
        </>
    );
};

export default MobileBottomCta;
